import { CellValue, ReportFinding } from './types';
import { RunFile, RunReading, RunSnapshot, RunStep, RunTrace, RunTraceRequest, firstValue, formatNumber, normalizeIdentifier, printValue, sameIdentifier, toNumber } from './runs';
import { BlockDefinition, BlockInput, defineBlock } from './blocks';
import { ReadingSelector, describeSelector, isEmptySelector, readingField, selectReadings } from './bindings';
import { Dimension, classify, classifyStep, dimensionRef } from './dimensions';
import { ReportLayout } from './layout';
import type { Composition } from './compose';
import { defineValues, v } from './schema';
import { finding } from './verdict';

/**
 * Results against limits: a value the run measured for each entry of a list
 * the procedure carries — an over-current envelope region by region, a
 * fall-time limit per LED string — judged against that entry's own limit,
 * one table per condition the run measured at (function × temperature).
 * Where the list gives each entry a time window, the capture of each
 * condition is drawn against the windows.
 *
 * Three blocks share one configuration. `limits` prints the results and
 * makes the checks; `limit-list` prints the limits themselves; `limit-chart`
 * draws each condition's capture against them. The last two name the
 * `limits` block they follow by its id in the layout, so the limits are set
 * once.
 *
 * A step's entry is the one it names ("Region 3", or the entry's name in the
 * list, "OC3 & t3"); its condition is its value of each dimension the block
 * groups by. An entry's limit is, in order: the one the measuring step
 * records (`limitReading`), the value the run entered for the entry (a step
 * naming it that records the list's column, such as "Enter the values for
 * LED short detection" under "LB_A"; under the column's name, or a longer one
 * that starts with it, `Upper_Limit_for_function` for `upper_limit`), then
 * the procedure's list — which can still hold the template's defaults. A
 * value can be held to one limit (at most, at least) or between two. Entries are the ones the run selected,
 * and any other the procedure offers that the run measured at. Nothing here
 * knows what the procedure measures.
 */

export const TIME_UNITS = ['s', 'ms', 'us'] as const;
export type TimeUnit = (typeof TIME_UNITS)[number];
const TIME_UNIT_LABELS: Record<TimeUnit, string> = { s: 'seconds', ms: 'milliseconds', us: 'microseconds' };
const SECONDS: Record<TimeUnit, number> = { s: 1, ms: 1e-3, us: 1e-6 };
const secondsPer = (unit: string): number => SECONDS[unit as TimeUnit] ?? 1;

const DIRECTIONS = ['at-most', 'at-least', 'between'] as const;
export type LimitDirection = (typeof DIRECTIONS)[number];

const limitsProps = defineValues({
  title: v.text({ label: 'Title', optional: true }),
  value: readingField({ label: 'The measured value', help: 'The reading each entry is judged by, e.g. the maximum current recorded in a region.' }),
  lowValue: readingField({ label: 'The value held to the lower limit, between two', optional: true, help: 'Where the procedure records a lowest beside a highest; else the measured value is held to both.' }),
  list: v.text({ label: 'The procedure’s list of limits', help: 'The identifier of a list (or choice) the procedure carries, one entry per limit, e.g. regions.', optional: true }),
  limitColumn: v.text({ label: 'Its column holding the limit (the upper one, between two)', optional: true }),
  lowerColumn: v.text({ label: 'Its column holding the lower limit, between two', optional: true }),
  nameColumn: v.text({ label: 'Its column naming each entry', optional: true }),
  windowStart: v.text({ label: 'Its column holding where each entry’s window starts', optional: true }),
  windowLength: v.text({ label: 'Its column holding how long the window is', optional: true }),
  windowEnd: v.text({ label: 'Or its column holding where the window ends', optional: true }),
  timeUnit: v.choice(TIME_UNITS, { label: 'The window columns are in', default: 's', labels: TIME_UNIT_LABELS }),
  per: dimensionRef('Or the dimension the limits are listed by', { optional: true, help: 'Where the procedure carries no list: the entries are this dimension’s values, each judged against the limit its step records.' }),
  limitReading: readingField({ label: 'Read the limit from the measuring step instead', optional: true }),
  lowerReading: readingField({ label: 'Read the lower limit from the measuring step instead', optional: true }),
  groupBy: v.list(dimensionRef('Dimension'), { label: 'One table per condition of', help: 'The dimensions a condition is made of, e.g. function and temperature.' }),
  unit: v.text({ label: 'Unit', optional: true }),
  direction: v.choice(DIRECTIONS, { label: 'Each value must be', default: 'at-most', labels: { 'at-most': 'at most its limit', 'at-least': 'at least its limit', between: 'between its lower and upper limits' } }),
  summary: v.boolean({ label: 'Open with the most extreme value and the smallest margin', default: true }),
});

export type LimitsProps = ReturnType<typeof limitsProps.defaults>;

export interface LimitEntry {
  key: string;
  /** "Region 3" */
  label: string;
  /** the list's name for it, "OC3 & t3"; '' where it names none */
  name: string;
  limit: number | null;
  /** where the limit came from: the measuring step (on a row), what the run entered for the entry, or the procedure's list */
  limitSource: LimitSource;
  /** the procedure's list's own value, where it gives one */
  listed: number | null;
  /** the lower limit, where each value must be between two; null otherwise */
  lower: number | null;
  lowerSource: LimitSource;
  listedLower: number | null;
  /** the entry's window, in seconds from the start of the capture; null where neither the run nor the list gives one */
  from: number | null;
  to: number | null;
  /** offered by the procedure but not among the run's selection; kept only where the run measured at it */
  offered: boolean;
}

export type LimitSource = 'step' | 'run' | 'list' | null;

export type LimitResult = 'PASS' | 'FAIL' | 'NOT RUN' | 'NO LIMIT';

export interface LimitRow extends LimitEntry {
  value: number | null;
  /** the value held to the lower limit, between two: the `lowValue` reading, else the value itself */
  low: number | null;
  /** the headroom to the limit as a percentage of it — under an upper limit, over a lower one; negative where it fails */
  marginPct: number | null;
  result: LimitResult;
  /** the step it was recorded at */
  stepTitle: string | null;
  /** the capture's exact peak inside the window, where a limit chart following this block drew one */
  captureMax: number | null;
}

export interface LimitGroup {
  key: string;
  /** the step's value of each dimension the block groups by, in order; '' where it names none */
  conditions: string[];
  /** "Turn Indicator, Nominal Temperature"; '' where the block groups by nothing */
  label: string;
  rows: LimitRow[];
  judged: number;
  failed: number;
  /** the smallest margin */
  worst: LimitRow | null;
  /** the highest value against an upper limit, the lowest against a lower one; null between two */
  extreme: LimitRow | null;
  result: 'PASS' | 'FAIL' | 'NOT RUN';
}

export interface LimitsModel {
  entries: LimitEntry[];
  groups: LimitGroup[];
  worst: { group: LimitGroup; row: LimitRow } | null;
  extreme: { group: LimitGroup; row: LimitRow } | null;
  /** values recorded at an entry with no limit, which are not judged */
  unjudged: Array<{ group: LimitGroup; row: LimitRow }>;
  /** every entry has a window: a capture can be drawn against them */
  windowed: boolean;
  /** at most its limit; false for at least */
  upper: boolean;
  direction: LimitDirection;
  /** why the block can't read the run: something not set in the layout */
  problem: string | null;
}

const text = (x: CellValue | undefined): string => (x === undefined || x === null ? '' : printValue(x).trim());
const precise = (n: number): number => Number(n.toPrecision(12));
const round1 = (n: number): number => Math.round(n * 10) / 10;

function numberIn(readings: RunReading[]): number | null {
  for (const r of readings) {
    const n = toNumber(firstValue(r));
    if (n !== null) return n;
  }
  return null;
}

interface RawEntry {
  label: string;
  name: string;
  offered: boolean;
  /** the list's columns for it */
  record: Record<string, CellValue>;
}

/** The entries as the procedure lists them: the run's selection, then what else it offers. Null where the procedure carries no such list. */
function listedEntries(run: Pick<RunSnapshot, 'parameters'>, props: LimitsProps, dimensions: Dimension[]): RawEntry[] | null {
  if (props.list) {
    const p = (run.parameters ?? []).find((x) => sameIdentifier(x.identifier, props.list));
    if (!p) return null;
    const selected = Array.from({ length: Math.max(p.values.length, p.records.length) }, (_, i): RawEntry => {
      const record = p.records[i] ?? {};
      return { label: text(p.values[i]) || text(record.title) || text(record.name) || String(i + 1), name: props.nameColumn ? text(record[props.nameColumn]) : '', offered: false, record };
    });
    const others = (p.choices ?? []).map(text).filter((c) => c && !selected.some((e) => e.label.toLowerCase() === c.toLowerCase()));
    return [...selected, ...others.map((label) => ({ label, name: '', offered: true, record: {} }))];
  }
  const per = dimensions.find((d) => d.id === props.per);
  if (!per) return null;
  return [...per.values.map((d) => ({ label: d.label, name: '', offered: false, record: {} })), ...(per.offered ?? []).map((d) => ({ label: d.label, name: '', offered: true, record: {} }))];
}

const entriesAsDimension = (entries: Array<Pick<RawEntry, 'label' | 'name'>>): Dimension => ({ id: 'entry', label: 'Entry', values: entries.map((e) => ({ label: e.label, match: e.name ? [e.name] : [] })) });

/**
 * What the block's entries are, each with its limit and window: what the run
 * entered for the entry where it did, else the procedure's list. Entries the
 * procedure offers beyond the run's selection are included; `limitResults`
 * keeps those the run measured at.
 */
export function limitEntries(run: Pick<RunSnapshot, 'parameters' | 'steps'>, props: LimitsProps, dimensions: Dimension[]): LimitEntry[] {
  const raw = listedEntries(run, props, dimensions) ?? [];
  const entryDim = entriesAsDimension(raw);
  const columns = [props.limitColumn, props.lowerColumn, props.windowStart, props.windowLength, props.windowEnd].filter(Boolean);
  // what the run entered: the first step naming an entry that records one of the list's columns, under its name or a longer one
  const entered = new Map<string, number>();
  const longer = new Map<string, number>();
  for (const step of columns.length ? run.steps : []) {
    const entry = classifyStep(step, entryDim);
    if (!entry) continue;
    for (const column of columns) {
      const key = `${entry}|${column}`;
      const exact = numberIn(step.readings.filter((r) => sameIdentifier(r.identifier, column)));
      if (exact !== null && !entered.has(key)) entered.set(key, exact);
      const prefix = `${normalizeIdentifier(column)}_`;
      const extended = numberIn(step.readings.filter((r) => normalizeIdentifier(r.identifier).startsWith(prefix)));
      if (extended !== null && !longer.has(key)) longer.set(key, extended);
    }
  }
  const scale = secondsPer(props.timeUnit);
  const between = props.direction === 'between';
  return raw.map((e): LimitEntry => {
    const ran = (column: string) => (column ? entered.get(`${e.label}|${column}`) ?? longer.get(`${e.label}|${column}`) ?? null : null);
    const listed = (column: string) => (column ? toNumber(e.record[column] ?? null) : null);
    const resolve = (column: string) => {
      const own = ran(column);
      const fromList = listed(column);
      return { value: own ?? fromList, source: (own !== null ? 'run' : fromList !== null ? 'list' : null) as LimitSource, listed: fromList };
    };
    const from = resolve(props.windowStart).value;
    const length = resolve(props.windowLength).value;
    const to = resolve(props.windowEnd).value ?? (from !== null && length !== null ? from + length : null);
    const limit = resolve(props.limitColumn);
    const lower = between ? resolve(props.lowerColumn) : { value: null, source: null, listed: null };
    return {
      key: e.label,
      label: e.label,
      name: e.name,
      limit: limit.value,
      limitSource: limit.source,
      listed: limit.listed,
      lower: lower.value,
      lowerSource: lower.source,
      listedLower: lower.listed,
      from: from === null ? null : precise(from * scale),
      to: to === null ? null : precise(to * scale),
      offered: e.offered,
    };
  });
}

/**
 * One value judged against its limits: under the upper, over the lower, or
 * both. The margin is the smallest headroom to a limit, as a percentage of it.
 */
function judgeValue(direction: LimitDirection, value: number | null, low: number | null, limit: number | null, lower: number | null): Pick<LimitRow, 'marginPct' | 'result'> {
  const ceiling = direction === 'at-least' ? null : limit;
  const floor = direction === 'at-least' ? limit : direction === 'between' ? lower : null;
  const lowest = direction === 'between' ? low ?? value : value;
  if (value === null && lowest === null) return { marginPct: null, result: 'NOT RUN' };
  const sides = [
    ...(ceiling !== null && value !== null ? [{ ok: value <= ceiling, margin: ceiling === 0 ? null : ((ceiling - value) / Math.abs(ceiling)) * 100 }] : []),
    ...(floor !== null && lowest !== null ? [{ ok: lowest >= floor, margin: floor === 0 ? null : ((lowest - floor) / Math.abs(floor)) * 100 }] : []),
  ];
  if (sides.length === 0) return { marginPct: null, result: 'NO LIMIT' };
  const margins = sides.flatMap((x) => (x.margin === null ? [] : [x.margin]));
  return { marginPct: margins.length ? round1(Math.min(...margins)) : null, result: sides.every((x) => x.ok) ? 'PASS' : 'FAIL' };
}

interface Scope {
  entries: LimitEntry[];
  /** the entries as a dimension, to find a step's */
  entryDim: Dimension;
  groupDims: Dimension[];
  problem: string | null;
}

function scopeOf(input: BlockInput<LimitsProps>): Scope {
  const { props, dimensions, run } = input;
  const none = (problem: string): Scope => ({ entries: [], entryDim: { id: 'entry', label: 'Entry', values: [] }, groupDims: [], problem });
  if (isEmptySelector(props.value as ReadingSelector)) return none('Pick the measured value in the report’s layout.');
  if (!props.list && !props.per) return none('Name the procedure’s list of limits, or the dimension the limits are listed by, in the report’s layout.');
  if (!props.list && !dimensions.some((d) => d.id === props.per)) return none(`“${props.per}” is not one of the report’s dimensions.`);
  const groupDims = props.groupBy.map((id) => dimensions.find((d) => d.id === id));
  const unknown = props.groupBy.filter((_, i) => !groupDims[i]);
  if (unknown.length) return none(`${unknown.map((u) => `“${u}”`).join(', ')} ${unknown.length === 1 ? 'is' : 'are'} not one of the report’s dimensions.`);
  const entries = limitEntries(run, props, dimensions);
  return { entries, entryDim: entriesAsDimension(entries), groupDims: groupDims as Dimension[], problem: null };
}

const groupKeyOf = (conditions: string[]): string => conditions.join('|');
const groupLabel = (conditions: string[]): string => conditions.filter(Boolean).join(', ');

/** The key a limit chart's capture of one condition is asked for, and handed back under, in `context.traces`. */
export const limitChartTraceKey = (chartBlockId: string, groupKey: string): string => `limits:${chartBlockId}:${groupKey}`;

/** The layout's limit charts that follow a `limits` block. */
function chartsFollowing(input: Pick<BlockInput, 'settings'>, limitsBlockId: string) {
  const layout = (input.settings as { layout?: ReportLayout }).layout;
  return (layout?.sections ?? []).flatMap((s) => s.blocks).filter((b) => b.type === 'limit-chart' && (b.props as { limits?: unknown }).limits === limitsBlockId);
}

/** Each condition the run measured at, every entry judged against its limit. Pure; the block's checks and its view read the same. */
export function limitResults(input: BlockInput<LimitsProps>): LimitsModel {
  const { props, run } = input;
  const scope = scopeOf(input);
  const { entryDim, groupDims, problem } = scope;
  const direction = (props.direction || 'at-most') as LimitDirection;
  const upper = direction !== 'at-least';
  if (problem) return { entries: [], groups: [], worst: null, extreme: null, unjudged: [], windowed: false, upper, direction, problem };
  const read = (step: RunStep, selector: unknown) => (isEmptySelector(selector as ReadingSelector) ? null : numberIn(selectReadings(step, selector as ReadingSelector)));
  const worse = (a: number | null, b: number | null, higher: boolean) => (a === null ? b : b === null ? a : higher ? Math.max(a, b) : Math.min(a, b));

  // what each condition recorded at each entry; one recorded twice keeps the worse
  const measured = new Map<string, { conditions: string[]; at: Map<string, { value: number | null; low: number | null; limit: number | null; lower: number | null; stepTitle: string }> }>();
  for (const step of run.steps) {
    const value = read(step, props.value);
    const low = direction === 'between' ? read(step, props.lowValue) : null;
    if (value === null && low === null) continue;
    const entry = classifyStep(step, entryDim, run);
    if (!entry) continue;
    const at = classify(step, groupDims, run);
    const conditions = groupDims.map((d) => at[d.id] ?? '');
    const key = groupKeyOf(conditions);
    const group = measured.get(key) ?? { conditions, at: new Map() };
    measured.set(key, group);
    const previous = group.at.get(entry);
    group.at.set(entry, {
      value: worse(previous?.value ?? null, value, upper),
      low: worse(previous?.low ?? null, low, false),
      limit: previous?.limit ?? read(step, props.limitReading),
      lower: previous?.lower ?? read(step, props.lowerReading),
      stepTitle: previous?.stepTitle ?? step.title,
    });
  }

  // an entry the procedure offers beyond the run's selection is in the block where the run measured at it
  const entries = scope.entries.filter((e) => !e.offered || Array.from(measured.values()).some((g) => g.at.has(e.key)));
  const charts = chartsFollowing(input, input.block.id);
  const groups = Array.from(measured.entries()).map(([key, g]): LimitGroup => {
    const trace = charts.map((c) => input.context.traces?.[limitChartTraceKey(c.id, key)]).find((t) => t && !t.error);
    const rows = entries.map((e, i): LimitRow => {
      const m = g.at.get(e.key);
      const limit = m?.limit ?? e.limit;
      const lower = m?.lower ?? e.lower;
      const value = m?.value ?? null;
      const low = direction === 'between' ? m?.low ?? value : null;
      const captured = trace?.windowMax?.[i];
      return {
        ...e,
        limit,
        limitSource: m?.limit !== null && m?.limit !== undefined ? 'step' : e.limitSource,
        lower,
        lowerSource: m?.lower !== null && m?.lower !== undefined ? 'step' : e.lowerSource,
        value,
        low,
        ...judgeValue(direction, value, low, limit, lower),
        stepTitle: m?.stepTitle ?? null,
        captureMax: captured === undefined || captured === null ? null : precise(captured),
      };
    });
    const judged = rows.filter((r) => r.result === 'PASS' || r.result === 'FAIL');
    const failed = judged.filter((r) => r.result === 'FAIL').length;
    const worst = judged.filter((r) => r.marginPct !== null).reduce<LimitRow | null>((w, r) => (!w || (r.marginPct as number) < (w.marginPct as number) ? r : w), null);
    const extreme = direction === 'between' ? null : judged.reduce<LimitRow | null>((x, r) => (!x || (upper ? (r.value as number) > (x.value as number) : (r.value as number) < (x.value as number)) ? r : x), null);
    return { key, conditions: g.conditions, label: groupLabel(g.conditions), rows, judged: judged.length, failed, worst, extreme, result: judged.length === 0 ? 'NOT RUN' : failed ? 'FAIL' : 'PASS' };
  });

  const all = groups.flatMap((group) => group.rows.filter((row) => row.marginPct !== null).map((row) => ({ group, row })));
  const worst = all.reduce<LimitsModel['worst']>((w, x) => (!w || (x.row.marginPct as number) < (w.row.marginPct as number) ? x : w), null);
  const extreme = direction === 'between' ? null : all.reduce<LimitsModel['extreme']>((p, x) => (!p || (upper ? (x.row.value as number) > (p.row.value as number) : (x.row.value as number) < (p.row.value as number)) ? x : p), null);
  const unjudged = groups.flatMap((group) => group.rows.filter((row) => row.result === 'NO LIMIT').map((row) => ({ group, row })));
  const windowed = entries.length > 0 && entries.every((e) => e.from !== null && e.to !== null);
  return { entries, groups, worst, extreme, unjudged, windowed, upper, direction, problem: null };
}

/** The unit the entries' windows read best in: milliseconds for an envelope that ends within a second. */
export function windowUnit(entries: Array<Pick<LimitEntry, 'to'>>): { unit: 's' | 'ms' | 'µs'; perSecond: number } {
  const end = Math.max(0, ...entries.flatMap((e) => (e.to === null ? [] : [Math.abs(e.to)])));
  if (end === 0 || end >= 1) return { unit: 's', perSecond: 1 };
  return end >= 1e-3 ? { unit: 'ms', perSecond: 1e3 } : { unit: 'µs', perSecond: 1e6 };
}

/** Seconds per unit of a capture's first column. */
export const secondsPerUnit = secondsPer;

/** How an entry is named in a sentence: "Region 3 (OC3 & t3)". */
export const entryName = (e: Pick<LimitEntry, 'label' | 'name'>): string => (e.name && e.name !== e.label ? `${e.label} (${e.name})` : e.label);
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
/** entries named in a finding before the rest are counted */
const LISTED = 4;

/** The run entered a limit of its own for the entry that the procedure's list gives otherwise (the template's default). */
export const enteredOtherwise = (e: LimitEntry): boolean =>
  (e.limitSource === 'run' && e.listed !== null && e.listed !== e.limit) || (e.lowerSource === 'run' && e.listedLower !== null && e.listedLower !== e.lower);

function limitFindings(input: BlockInput<LimitsProps>): ReportFinding[] {
  const m = limitResults(input);
  if (m.problem) return [];
  const id = `limits-${input.block.id}`;
  const unit = input.props.unit ? ` ${input.props.unit}` : '';
  const fmt = (n: number | null) => (n === null ? '—' : `${formatNumber(n)}${unit}`);
  const judged = m.groups.reduce((n, g) => n + g.judged, 0);
  const out: ReportFinding[] = [];
  const list = input.props.list;
  if (judged === 0) {
    const carried = !list || (input.run.parameters ?? []).some((p) => sameIdentifier(p.identifier, list));
    out.push(finding.gap(id, !carried
      ? `The run carries no “${list}” list, so nothing it measured can be judged against a limit.`
      : `No step records ${describeSelector(input.props.value as ReadingSelector)} at ${list ? `an entry of the procedure’s “${list}” list` : 'an entry'} with a limit, so nothing is judged against one.`));
  }
  const missed = (r: LimitRow): string[] => (m.direction !== 'between'
    ? [`${entryName(r)} recorded ${fmt(r.value)} against ${fmt(r.limit)}`]
    : [
        ...(r.value !== null && r.limit !== null && r.value > r.limit ? [`${entryName(r)} recorded ${fmt(r.value)} against an upper limit of ${fmt(r.limit)}`] : []),
        ...(r.low !== null && r.lower !== null && r.low < r.lower ? [`${entryName(r)} recorded ${fmt(r.low)} against a lower limit of ${fmt(r.lower)}`] : []),
      ]);
  for (const g of m.groups.filter((x) => x.failed > 0)) {
    const over = g.rows.filter((r) => r.result === 'FAIL').flatMap(missed);
    out.push(finding.defect(`${id}-${g.key || 'all'}`, `The limit was not met${g.label ? ` at ${g.label}` : ''}: ${over.join('; ')}.`));
  }
  if (judged > 0 && m.groups.every((g) => g.failed === 0) && m.worst) {
    const at = input.props.groupBy.length ? ` at ${plural(m.groups.length, 'condition')}` : '';
    out.push(finding.pass(id, `Every recorded value is within its limit: ${plural(judged, 'value')}${at}; the smallest margin is ${formatNumber(m.worst.row.marginPct as number)} %, in ${entryName(m.worst.row)}${m.worst.group.label ? ` at ${m.worst.group.label}` : ''}.`));
  }
  // the run entered limits of its own where the procedure's list still holds others (a template's defaults)
  const differing = m.entries.filter(enteredOtherwise);
  if (differing.length) {
    const limits = (e: LimitEntry, which: 'limit' | 'lower') => (which === 'limit' ? [e.limit, e.listed] : [e.lower, e.listedLower]);
    const named = differing.slice(0, LISTED).map((e) => (['lower', 'limit'] as const)
      .filter((which) => (which === 'limit' ? e.limitSource : e.lowerSource) === 'run' && limits(e, which)[1] !== null && limits(e, which)[0] !== limits(e, which)[1])
      .map((which) => `${entryName(e)} ${fmt(limits(e, which)[0])} (list: ${fmt(limits(e, which)[1])})`).join(', ')).join('; ');
    out.push(finding.note(`${id}-entered`, `The run entered its own limit for ${plural(differing.length, 'entry', 'entries')}, which the procedure’s list gives otherwise; the run’s are applied: ${named}${differing.length > LISTED ? `; and ${differing.length - LISTED} more` : ''}.`));
  }
  if (m.unjudged.length) {
    const names = Array.from(new Set(m.unjudged.map((u) => entryName(u.row))));
    out.push(finding.gap(`${id}-no-limit`, `${names.join(', ')} ${names.length === 1 ? 'has' : 'have'} a recorded value but no limit, so ${names.length === 1 ? 'it is' : 'they are'} not judged.`));
  }
  return out;
}

export const limitsBlock: BlockDefinition<LimitsProps> = defineBlock({
  type: 'limits',
  title: 'Results against limits',
  description: 'A measured value for each entry of a list of limits the procedure carries (a region of an envelope, an LED string), judged against that entry’s limit, one table per condition — with the margin to each limit.',
  group: 'Results',
  props: limitsProps,
  findings: limitFindings,
  isEmpty: (input) => limitResults(input).groups.length === 0,
});

/** What a composition's `limits` block found, by the block's id — for a document's summary tiles; null where the layout has no such block. */
export function limitsOf(c: Pick<Composition, 'sections'>, id: string): LimitsModel | null {
  const found = c.sections.flatMap((s) => s.blocks).find((b) => b.block.id === id && b.block.type === 'limits');
  return found ? limitResults(found.input as BlockInput<LimitsProps>) : null;
}

// ── following a limits block ────────────────────────────────────────────────

/** The `limits` block a list or chart follows, as that block reads the run; null where the layout has no such block. */
export function followedLimits(input: BlockInput<{ limits: string }>): { input: BlockInput<LimitsProps>; scope: Scope; model: LimitsModel } | null {
  const layout = (input.settings as { layout?: ReportLayout }).layout;
  const block = (layout?.sections ?? []).flatMap((s) => s.blocks).find((b) => b.id === input.props.limits && b.type === 'limits');
  if (!block) return null;
  const followed: BlockInput<LimitsProps> = { ...input, block, props: limitsBlock.props.normalize(block.props) };
  return { input: followed, scope: scopeOf(followed), model: limitResults(followed) };
}

const followProblem = (id: string) => (id ? `The layout has no “limits” block with the id “${id}” to follow.` : 'Name the “limits” block this follows, by its id in the layout.');

export const limitListBlock = defineBlock({
  type: 'limit-list',
  title: 'Limits',
  description: 'The limits a “limits” block judges against, entry by entry, with each entry’s window where the procedure gives one.',
  group: 'Results',
  props: defineValues({
    limits: v.text({ label: 'The limits block it lists', help: 'Its id in the layout.' }),
    title: v.text({ label: 'Title', optional: true }),
  }),
  isEmpty: (input) => (followedLimits(input)?.model.entries.length ?? 0) === 0,
});

export const limitListProblem = (input: BlockInput<{ limits: string }>): string | null => (followedLimits(input) ? null : followProblem(input.props.limits));

// ── the capture against the limits ──────────────────────────────────────────

const limitChartProps = defineValues({
  limits: v.text({ label: 'The limits block whose limits it draws', help: 'Its id in the layout.' }),
  file: readingField({ label: 'The capture', help: 'The points file each condition recorded, e.g. the switch-on current.' }),
  xUnit: v.choice(TIME_UNITS, { label: 'The capture’s first column is in', default: 's', labels: TIME_UNIT_LABELS }),
  title: v.text({ label: 'Caption', optional: true }),
  seriesLabel: v.text({ label: 'What the capture measures', optional: true, help: 'e.g. DUT input current (A)' }),
  zoom: v.boolean({ label: 'Draw the short windows again, zoomed in', default: true }),
});

export type LimitChartProps = ReturnType<typeof limitChartProps.defaults>;

/** At most this many conditions are drawn: the server reads each capture on every view of the report. */
export const MAX_LIMIT_CHARTS = 8;

export interface LimitCapture {
  group: LimitGroup;
  file: RunFile | null;
  traceKey: string;
  trace: RunTrace | null;
}

export interface LimitChartModel {
  model: LimitsModel | null;
  captures: LimitCapture[];
  problem: string | null;
}

/**
 * Each measured condition's capture: the points file the step names, recorded
 * at that condition but at no single entry — a file recorded at one region
 * is that region's zoom, not the capture of the whole switch-on.
 */
export function limitCharts(input: BlockInput<LimitChartProps>): LimitChartModel {
  const followed = followedLimits(input);
  if (!followed) return { model: null, captures: [], problem: followProblem(input.props.limits) };
  const { scope, model } = followed;
  if (model.problem) return { model, captures: [], problem: model.problem };
  if (isEmptySelector(input.props.file as ReadingSelector)) return { model, captures: [], problem: 'Pick the capture the chart draws in the report’s layout.' };
  const files = new Map<string, RunFile>();
  for (const step of input.run.steps) {
    const file = selectReadings(step, input.props.file as ReadingSelector).flatMap((r) => r.attachments ?? []).find((f) => f.kind === 'data');
    if (!file || classifyStep(step, scope.entryDim, input.run)) continue;
    const at = classify(step, scope.groupDims, input.run);
    const key = groupKeyOf(scope.groupDims.map((d) => at[d.id] ?? ''));
    if (!files.has(key)) files.set(key, file);
  }
  const captures = model.groups.slice(0, MAX_LIMIT_CHARTS).map((group): LimitCapture => {
    const traceKey = limitChartTraceKey(input.block.id, group.key);
    const trace = input.context.traces?.[traceKey];
    return { group, file: files.get(group.key) ?? null, traceKey, trace: trace && !trace.error && trace.points.length > 0 ? trace : null };
  });
  return { model, captures, problem: null };
}

/** A recorded value and the capture's own peak in its window disagree where they differ by more than this share of the recorded value. */
const DISAGREEMENT = 0.1;

export const limitChartBlock: BlockDefinition<LimitChartProps> = defineBlock({
  type: 'limit-chart',
  title: 'Capture against limits',
  description: 'Each condition’s capture drawn against the windows and limits of a “limits” block, with the value recorded in each window — and a note where the recorded value and the capture disagree.',
  group: 'Results',
  props: limitChartProps,
  traces: (input): RunTraceRequest[] => {
    const charts = limitCharts(input);
    if (!charts.model) return [];
    const perSecond = 1 / secondsPer(input.props.xUnit);
    const windows = charts.model.entries.map((e): [number, number] => (e.from === null || e.to === null ? [0, 0] : [precise(e.from * perSecond), precise(e.to * perSecond)]));
    return charts.captures.filter((c) => c.file).map((c) => ({ key: c.traceKey, fileId: (c.file as RunFile).fileId, windows }));
  },
  findings: (input) => {
    const charts = limitCharts(input);
    const unit = followedLimits(input)?.input.props.unit;
    const fmt = (n: number) => `${formatNumber(n)}${unit ? ` ${unit}` : ''}`;
    // a difference smaller than a hundredth of the limit is the capture's resolution, not a disagreement
    const disagreeing = charts.captures.flatMap((c) => c.group.rows
      .filter((r) => r.captureMax !== null && r.value !== null && Math.abs(r.captureMax - r.value) > Math.max(DISAGREEMENT * Math.abs(r.value), 0.01 * Math.abs(r.limit ?? 0)))
      .map((r) => `${entryName(r)}${c.group.label ? ` at ${c.group.label}` : ''} (recorded ${fmt(r.value as number)}, capture ${fmt(r.captureMax as number)})`));
    return disagreeing.length
      ? [finding.note(`limit-chart-${input.block.id}`, `The value recorded in ${disagreeing.join('; ')} differs from the capture’s own peak in that window by more than ${DISAGREEMENT * 100} % — check which value the record should carry.`)]
      : [];
  },
  isEmpty: (input) => limitCharts(input).captures.every((c) => !c.trace),
});

export const LIMIT_BLOCKS = [limitsBlock, limitListBlock, limitChartBlock];
